import styled from '@emotion/styled';
import { Link as OriginalLink, useLocation } from 'react-router-dom';
import { companyUserContext } from 'application/contexts/useCompanyUser';
import LogoImg from 'ui/images/logo.svg';
import { useState, useEffect, useContext } from 'react';
import { mqUser } from 'utils/responsive';
import { SubsidyInfoBanner } from './SubsidyInfoBanner';

const Wrapper = styled.div`
    position: relative;
    height: 100%;
    background: var(--main-color-blue);
    padding: 15px 0 10px 0;
    width: 100%;
    transition: transform 0.3s ease-in-out;

    ${mqUser('SP')} {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 1000;
        transform: translateX(-100%);
        width: 100%;
        max-width: 500px;
        height: 100vh;
        @supports (height: 100svh) {
            height: 100svh;
        }
        visibility: hidden;

        &.open {
            transform: translateX(0);
            visibility: visible;
        }
    }
`;

const Logo = styled.div`
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0 auto 12px auto;
    display: flex;

    img {
        height: 55px;
    }
`;

const Link = styled(OriginalLink)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 auto;
    padding-left: 25px;
    font-weight: 700;
    line-height: 50px;
    letter-spacing: 0%;
    cursor: pointer;
    color: var(--white);
    text-decoration: none;
    position: relative;

    &:hover {
        background: var(--main-color-blue-light);
    }

    ${mqUser('SP')} {
        font-size: 1.2rem;
        line-height: 60px;
    }
`;

const Text = styled.span`
    position: relative;
`;

const NoLink = styled.a`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 auto;
    padding-left: 25px;
    font-weight: 700;
    line-height: 50px;
    letter-spacing: 0%;
    cursor: pointer;
    color: var(--white);
    text-decoration: none;

    &:hover {
        background: var(--main-color-blue-light);
    }

    ${mqUser('SP')} {
        font-size: 1.2rem;
        line-height: 60px;
    }
`;

const MenuButton = styled.button`
    display: none;
    position: fixed;
    top: 10px;
    left: 20px;
    z-index: 1001;
    background: none;
    border: none;
    cursor: pointer;
    padding: 10px;

    ${mqUser('SP')} {
        display: block;
    }

    span {
        display: block;
        width: 25px;
        height: 3px;
        background: var(--main-color-blue);
        margin: 5px 0;
        transition: 0.3s;
    }

    ${mqUser('SP')} {
        &.open span {
            background: var(--white);
        }
    }
`;

const Overlay = styled.div`
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

    ${mqUser('SP')} {
        display: block;

        &.open {
            opacity: 1;
            visibility: visible;
        }
    }
`;

const Dot = styled.span`
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: linear-gradient(48deg, #ffd700, #ffa500, #ffd700, #ffa500);
    background-size: 500%;
    flex-shrink: 0;
    box-shadow: 0 0 6px rgba(255, 165, 0, 0.6);
    animation: glow 2s ease-in-out infinite;
    margin-left: 5px;

    @keyframes glow {
        0% {
            box-shadow: 0 0 6px rgba(255, 165, 0, 0.6);
            transform: translateY(-50%) scale(1);
        }
        50% {
            box-shadow: 0 0 12px rgba(255, 165, 0, 0.8);
            transform: translateY(-50%) scale(1.1);
        }
        100% {
            box-shadow: 0 0 6px rgba(255, 165, 0, 0.6);
            transform: translateY(-50%) scale(1);
        }
    }
`;

const BannerWrapper = styled.div`
    margin-top: 50px;
`;

export const Sidebar: React.FC = () => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const { user, existsUnreadMsg, offerList, notCompletedProfile } = useContext(companyUserContext);

    const isActive = (path: string, exact = false) => {
        return exact ? location.pathname === path : location.pathname.startsWith(path);
    };

    const goToDashboard = () => {
        window.location.href = notCompletedProfile ? '/company/registration/user_profile/' : '/company/dashboard/';
    };

    const goToContactPage = () => {
        window.open('/contact/', '_blank');
    };

    const toggleMenu = () => {
        if (isOpen) {
            closeMenu();
        } else {
            setIsOpen(true);
        }
    };

    const closeMenu = () => {
        setIsOpen(false);
        document.body.style.overflow = 'auto';
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 799) {
                closeMenu();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    return (
        <>
            <MenuButton onClick={toggleMenu} className={isOpen ? 'open' : ''}>
                <span></span>
                <span></span>
                <span></span>
            </MenuButton>
            <Overlay onClick={closeMenu} className={isOpen ? 'open' : ''} />
            <Wrapper className={isOpen ? 'open' : ''}>
                <Logo onClick={goToDashboard}>
                    <img src={LogoImg} alt="MAny" />
                </Logo>

                <NoLink
                    href="#"
                    onClick={() => {
                        goToDashboard();
                        closeMenu();
                    }}
                    className={isActive('/company/dashboard/', true) ? 'active' : ''}
                >
                    マイページ
                </NoLink>
                <Link
                    to={notCompletedProfile ? '/company/registration/user_profile/' : '/company/match/'}
                    className={isActive('/company/match/') ? 'active' : ''}
                    onClick={closeMenu}
                >
                    <Text>オファー一覧</Text>
                    {offerList.length > 0 && <Dot />}
                </Link>
                <Link
                    to={notCompletedProfile ? '/company/registration/user_profile/' : '/company/message/'}
                    className={!isActive('/company/message/') && isActive('/company/message/') ? 'active' : ''}
                    onClick={closeMenu}
                >
                    <Text>メッセージ</Text>
                    {existsUnreadMsg && <Dot />}
                </Link>
                <Link
                    to={notCompletedProfile ? '/company/registration/user_profile/' : '/company/edit/'}
                    className={isActive('/company/edit/') ? 'active' : ''}
                    onClick={closeMenu}
                >
                    企業情報
                </Link>
                <Link
                    to={notCompletedProfile ? '/company/registration/user_profile/' : '/company/member/'}
                    className={isActive('/company/member/') ? 'active' : ''}
                    onClick={closeMenu}
                >
                    メンバー管理
                </Link>
                {user?.company?.supporter_user && (
                    <Link
                        to={'/company/support_chat/'}
                        className={
                            !isActive('/company/support_chat/') && isActive('/company/support_chat/') ? 'active' : ''
                        }
                        onClick={closeMenu}
                    >
                        <Text>サポートチャット</Text>
                    </Link>
                )}
                <NoLink
                    href="#"
                    onClick={() => {
                        goToContactPage();
                        closeMenu();
                    }}
                >
                    お問い合わせ
                </NoLink>
                <BannerWrapper>
                    <SubsidyInfoBanner />
                </BannerWrapper>
            </Wrapper>
        </>
    );
};
